import Vue from 'vue'
import Router from 'vue-router'
import { getToken, removeToken } from '@/utils/auth'
import { getUrlMerchantId, setUrlMerchantId, getUserInfo, clearStorage } from '@/utils/storage'
import store from '@/store'

import Index from '@/pages/index/index.vue'
import Landing from '@/pages/landing/landing.vue'
import Login from '@/pages/user/login.vue'
import Profile from '@/pages/user/profile.vue'
import Feedback from '@/pages/user/feedback.vue'
import Traveler from '@/pages/user/traveler.vue'
import ProductList from '@/pages/product/list.vue'
import ProductDetail from '@/pages/product/detail.vue'
import ProductCalendar from '@/pages/product/calendar.vue'
import OrderList from '@/pages/order/list.vue'
import OrderCreate from '@/pages/order/create.vue'
import OrderDetail from '@/pages/order/detail.vue'
import OrderPayment from '@/pages/order/payment.vue'
import ArticleList from '@/pages/article/list.vue'
import ArticleDetail from '@/pages/article/detail.vue'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    { path: '/', name: 'Index', component: Index },
    // /home/13?token=xxx 等同于首页 + merchantid=13，方便合作方直接拼商户ID路径传 token。
    // 注意：1）vue-router 3.x 的 alias 无法把动态参数从"/home/:merchantId"映射回零参数的"/"，
    // params 会丢失（已用 router.resolve 实测验证），所以这里用独立路由而非 alias；
    // 2）参数正则约束 :merchantId(\d+) 在当前 vue-router 版本下会导致路由完全无法匹配（同样实测验证），故不加约束，
    // 非数字值只是落到首页当无效商户号处理，不会报错，可接受。
    // 复用 Index 组件，App.vue/bottom-nav.vue 的底部导航按 route name 判断，需把 IndexWithMerchant 加入白名单。
    { path: '/home/:merchantId', name: 'IndexWithMerchant', component: Index },
    { path: '/landing', name: 'Landing', component: Landing },
    { path: '/login', name: 'Login', component: Login },
    { path: '/profile', name: 'Profile', component: Profile },
    { path: '/user/feedback', name: 'Feedback', component: Feedback },
    { path: '/traveler', name: 'Traveler', component: Traveler },
    { path: '/products/:id?', name: 'ProductList', component: ProductList },
    { path: '/product/:id', name: 'ProductDetail', component: ProductDetail },
    { path: '/product/:id/calendar', name: 'ProductCalendar', component: ProductCalendar },
    { path: '/orders', name: 'OrderList', component: OrderList },
    { path: '/order/create/:id', name: 'OrderCreate', component: OrderCreate },
    { path: '/order/:id', name: 'OrderDetail', component: OrderDetail },
    { path: '/order/payment/:id', name: 'OrderPayment', component: OrderPayment },
    { path: '/articles', name: 'ArticleList', component: ArticleList },
    { path: '/article/:id', name: 'ArticleDetail', component: ArticleDetail }
  ]
})

const publicRoutes = ['/login', '/landing']

router.beforeEach(async (to, from, next) => {
  // 任意路由携带 merchantid 参数时持久化（不区分大小写）；/home/:merchantId 路由（如 /home/13）走路径参数
  const mid = to.query.merchantid || to.query.merchantId || to.query.MerchantId || to.params.merchantId

  // 已登录用户的商户号切换检测：sweetour_url_merchant_id 只控制 X-Merchant-Id 请求头（浏览哪个商户的公开数据），
  // 与"当前 token/userInfo 到底是哪个商户的登录态"是两件独立的事——历史上一直如此，只是过去没人会手动改 URL 里的
  // merchantid 还指望自动切换登录态，所以没暴露。现在 URL 可以是合作方跳转链接，必须在这里补上一致性检查：
  // 如果 URL 要求的商户与"当前已登录用户实际所属的商户"不一致，说明这个登录态对新商户是无效的，
  // 清掉 token/userInfo（不清 merchantId，因为 mid 马上就会被写成新值），走到下面"未登录"分支，
  // 该跳登录页就跳登录页；如果 URL 同时还带了合作方 token，紧接着的 token 登录会在此基础上重新签发新商户的登录态。
  if (mid) {
    const currentUserInfo = getUserInfo()
    const currentUserMerchantId = currentUserInfo && (currentUserInfo.merchantId || currentUserInfo.MerchantId)
    if (getToken() && currentUserMerchantId && String(currentUserMerchantId) !== String(mid)) {
      removeToken()
      clearStorage()
    }
    setUrlMerchantId(mid)
  }

  // 合作方跳转链接携带 token 参数（JWT，含 user_phone）时自动免密登录。
  // 合作方入口不固定（首页/菜单列表页/订单页等），链接里往往只有 token，没有 merchantId，
  // 所以这里不按商户号做前置过滤——任意路由只要带 token 就尝试登录，商户身份完全由后端按配置解析。
  // 校验失败静默忽略，按无 token 走原有登录逻辑；成功/失败都会清掉地址栏的 token 参数，避免刷新时重复触发。
  const partnerToken = to.query.token
  if (partnerToken) {
    try {
      await store.dispatch('user/partnerTokenLogin', { token: partnerToken })
    } catch (e) {
      // 静默忽略
    }
    const restQuery = { ...to.query }
    delete restQuery.token
    next({ path: to.path, query: restQuery, replace: true })
    return
  }

  // 裸 "/" 且当前及历史都没有商户上下文时，优先引导到落地页，而不是直接判未登录跳去登录页——
  // 落地页本身在 publicRoutes 白名单里，不需要登录态
  if (to.name === 'Index' && !(mid || getUrlMerchantId())) {
    next('/landing')
    return
  }

  const token = getToken()
  if (!token && !publicRoutes.includes(to.path)) {
    // 未登录跳转登录页，保留商户ID参数
    const merchantId = mid || getUrlMerchantId()
    const query = merchantId ? { merchantid: merchantId } : {}
    next({ path: '/login', query })
  } else {
    next()
  }
})

export default router
